import Service from '@ekd/lt-portal-connector/dist/Service';
import { getEnv } from '../../../common/utils/getEnv';
import { Config } from '../types/channel';
import { dataLoader } from '../../../libs/redis/dataloader/dataLoader';
import { AxiosRequestConfig } from 'axios';

class ChannelConfigService extends Service {
  /**
   * Fetch configuration
   */
  public async fetch<Response extends Config>(id: string) {
    try {
      const dataLoaderOptions = {
        local: {
          cache: true,
        },
        remote: {
          expireTimeMs: 30000, // 30 seconds
          gracePeriodMs: 24 * 3600 * 1000, // 24 hours
          keyPrefix: 'channel_config',
        },
        cacheKeyFn: () => {
          return `site_${id}`;
        },
      };

      const options = {
        url: this.getRequestUrl(id),
        timeout: 3000,
      };

      const requestWrapper = (options: AxiosRequestConfig) => {
        const apiProvider = this.createProvider('HTTP');
        return apiProvider.request<Response>(options);
      };

      const redisDataLoader = dataLoader<AxiosRequestConfig, Response>(requestWrapper, dataLoaderOptions);
      const config: Response = redisDataLoader ? await redisDataLoader.load(options) : await requestWrapper(options);

      if (!config?.settings) {
        return null;
      }

      config.settings.domain = id.replace('.test.delfi', '.delfi');

      if (id === 'www.delfi.lt') {
        const frontpageAdForm = config.page?.frontpage?.component?.adForm;
        const projectsAdsConfig = await this.fetchProjectsAdsConfig();

        if (Array.isArray(frontpageAdForm) && projectsAdsConfig.length) {
          config.page.frontpage.component.adForm = this.mergeAdFormConfig(frontpageAdForm, projectsAdsConfig);
        }
      }

      return config;
    } catch (error) {
      process.sentry?.captureException(`ChannelConfigService fetch(${id}) failed: ${error}`, {
        contexts: { data: { channelId: id } },
        tags: { 'process.type': process.server ? 'server' : 'client' },
      });
      return null;
    }
  }

  private getRequestUrl(id: string): string {
    const isTest = typeof window === 'undefined' ? process.env.CHANNEL_CONFIG_ENV === 'test' : /.test.delfi/.test(document.location.hostname);
    const domain = isTest ? id.replace('.test.delfi', '.delfi') : id;
    const channel = domain.split('/')[1] ?? 'delfi';

    const url = new URL(getEnv('staticConfigUrl'));
    url.pathname += `/${channel}.json`;

    return url.href;
  }

  private async fetchProjectsAdsConfig() {
    try {
      const options: AxiosRequestConfig = {
        url: `${getEnv('staticConfigUrl')}/ads/projects.json`,
        timeout: 3000,
      };
      const dataLoaderOptions = {
        local: {
          cache: true,
        },
        remote: {
          expireTimeMs: 30000, // 30 seconds
          gracePeriodMs: 24 * 3600 * 1000, // 24 hours
          keyPrefix: 'channel_config_ads',
        },
        cacheKeyFn: () => {
          return 'frontpage_ads_delfi';
        },
      };

      const requestWrapper = (requestOptions: AxiosRequestConfig) => {
        const apiProvider = this.createProvider('HTTP');
        return apiProvider.request<Record<string, any>[]>(requestOptions);
      };

      const redisDataLoader = dataLoader<AxiosRequestConfig, Record<string, any>[]>(requestWrapper, dataLoaderOptions);
      const adsConfig = redisDataLoader ? await redisDataLoader.load(options) : await requestWrapper(options);

      return Array.isArray(adsConfig) ? adsConfig : [];
    } catch (error) {
      process.sentry?.captureException(`ChannelConfigService fetchProjectsAdsConfig failed: ${error}`, {
        tags: { 'process.type': process.server ? 'server' : 'client' },
      });

      return [];
    }
  }

  private mergeAdFormConfig(currentConfig: Record<string, any>[], adsConfig: Record<string, any>[]) {
    const mergedConfig = currentConfig.map((item) => adsConfig.find((adItem) => adItem?.name === item?.name) || item);
    const newAdsConfig = adsConfig.filter((item) => !currentConfig.some((currentItem) => currentItem?.name === item?.name));

    return [...mergedConfig, ...newAdsConfig];
  }
}

export const channelConfigService = new ChannelConfigService();
